import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShoppingBag, Wallet, Globe, Loader2, ArrowRight, Sparkles } from 'lucide-react';
import { useTranslation, Trans } from 'react-i18next';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import { isStoresMobile } from '../lib/platform';
import { getShopConfig, createTopup } from '../api/shop';
import { createTopupCheckoutV2 } from '../api/walletTopupV2';
import { trackPixelEvent } from '../hooks/useMetaPixel';
import ReadOnlyNotice from '../components/ReadOnlyNotice';
import { canWrite } from '../lib/canWrite';
import { TOPUP_PACKAGES, fmtCredits, creditsToEur } from '../lib/currency';

// Phase 37A.5 — sessionStorage key that lets WalletPage know a v2 checkout
// is in flight after the redirect back from Stripe. Bounded lifetime — the
// polling logic in WalletPage clears it as soon as the session hits a
// terminal state (completed / cancelled / expired).
const V2_PENDING_KEY = 'nymia_pending_topup_v2';

/**
 * Shop / "Ricarica" page — Phase 17.5
 * Phase 44 · Feb 2026 — The refund request area has moved to the
 * WalletPage (below "Ordini"). Shop now focuses exclusively on top-up
 * discovery / package selection. The refund logic itself is unchanged
 * and lives in `<RefundRequestCard />`.
 */
const ShopPage = () => {
  const { user, refresh } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { t } = useTranslation('shop');
  const mobile = isStoresMobile();
  // Phase 30.B — packages are the EUR price + Credits delivered.
  const [packages, setPackages] = useState(TOPUP_PACKAGES);
  const [selected, setSelected] = useState(TOPUP_PACKAGES[1]?.eur ?? 10);
  const [busy, setBusy] = useState(null);
  // Phase 37A.5 — flag from /shop/config. When true, the top-up button
  // uses the new real-Stripe-Checkout v2 flow; otherwise it falls back
  // to the legacy /shop/topup + /checkout/:id path.
  const [v2Enabled, setV2Enabled] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const cfg = await getShopConfig();
        if (Array.isArray(cfg.topup_packages) && cfg.topup_packages.length) {
          setPackages(cfg.topup_packages);
          setSelected(cfg.topup_packages[Math.min(1, cfg.topup_packages.length - 1)].eur);
        }
        setV2Enabled(Boolean(cfg.wallet_topup_v2));
      } catch (_e) { /* keep defaults */ }
    })();
  }, []);

  const startTopup = async () => {
    if (mobile) return; // policy: no IAP on iOS/Android stores
    if (!writable) {
      toast({ title: t('topup.verify_email_title'), description: t('topup.verify_email_desc'), variant: 'destructive' });
      return;
    }
    setBusy('topup');
    try {
      if (v2Enabled) {
        // Phase 37A.5 — v2 branch: server creates a real Stripe Checkout
        // Session (or a mock one in preview) and returns the hosted URL.
        // We stash the internal id in sessionStorage so WalletPage can
        // pick it up after the redirect and poll for completion.
        const out = await createTopupCheckoutV2(selected, window.location.origin);
        // Phase 37E — InitiateCheckout (Meta funnel signal): client-only,
        // deterministic event_id keyed on the internal session id so
        // Meta collapses accidental duplicate clicks.
        trackPixelEvent(
          'InitiateCheckout',
          {
            value: creditsToEur(out.credits_amount || 0),
            currency: 'EUR',
            content_type: 'wallet_topup',
            content_ids: [`topup_${out.internal_session_id}`],
          },
          `initiate_checkout_topup_${out.internal_session_id}`,
        );
        try {
          sessionStorage.setItem(V2_PENDING_KEY, JSON.stringify({
            internal_session_id: out.internal_session_id,
            eur_amount: out.eur_amount,
            credits_amount: out.credits_amount,
            created_at: new Date().toISOString(),
          }));
        } catch (_e) { /* private mode — polling still works via query param */ }
        window.location.assign(out.checkout_url);
        return;
      }
      // Legacy branch — untouched.
      const out = await createTopup(selected);
      trackPixelEvent(
        'InitiateCheckout',
        {
          value: creditsToEur(selected),
          currency: 'EUR',
          content_type: 'wallet_topup',
          content_ids: [`topup_${out.session_id}`],
        },
        `initiate_checkout_topup_${out.session_id}`,
      );
      navigate(`/checkout/${out.session_id}`);
    } catch (e) {
      const detail = e.response?.data?.detail || '';
      toast({
        title: t('topup.start_error_title'),
        description: (typeof detail === 'string' ? detail.split('|').pop() : '') || t('topup.start_error_default'),
        variant: 'destructive',
      });
    } finally { setBusy(null); }
  };

  const balance = Number(user?.wallet_balance || 0);
  const writable = canWrite(user);

  return (
    <div className="mx-auto max-w-4xl px-3 py-6" data-testid="shop-page">
      <header className="mb-6 flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-500/15">
          <ShoppingBag className="h-5 w-5 gold-text" />
        </div>
        <div>
          <h1 className="text-2xl font-bold gold-text" data-testid="shop-title">{t('header.title')}</h1>
          <p className="text-sm text-muted-foreground">{t('header.subtitle')}</p>
        </div>
      </header>

      <ReadOnlyNotice action={t('read_only_action')} className="mb-4" />

      {/* F9.6 — Shop = solo acquisto crediti. Il saldo resta come contesto
          d'acquisto; "in maturazione" è stato ricollocato nel Pannello. */}
      <section className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="rounded-2xl border border-border bg-card p-4">
          <p className="text-[11px] uppercase tracking-wider text-muted-foreground">{t('kpi.available')}</p>
          <p className="text-3xl font-bold gold-text" data-testid="shop-available-balance">{fmtCredits(balance)}</p>
          <p className="mt-1 text-[11px] text-muted-foreground">{t('kpi.available_hint')}</p>
        </div>
        <div className="rounded-2xl border border-amber-500/30 bg-amber-500/5 p-4">
          <p className="text-[11px] uppercase tracking-wider gold-text">{t('kpi.security_label')}</p>
          <p className="text-sm font-semibold">{t('kpi.security_title')}</p>
          <p className="mt-1 text-[11px] text-muted-foreground">{t('kpi.security_hint')}</p>
        </div>
      </section>

      {/* Top-up area */}
      <section className="mt-6 rounded-2xl border border-border bg-card p-5" data-testid="topup-section">
        <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold gold-text">
          <Wallet className="h-4 w-4" /> {t('topup.section_title')}
        </h2>

        {mobile ? (
          <div
            className="flex items-start gap-3 rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 text-sm text-foreground"
            data-testid="topup-mobile-message"
          >
            <Globe className="mt-0.5 h-4 w-4 shrink-0 gold-text" />
            <div>
              {t('topup.mobile_disabled')}{' '}
              <span className="font-semibold">{t('topup.mobile_disabled_action')}</span>{' '}
              {t('topup.mobile_disabled_hint')}
            </div>
          </div>
        ) : (
          <>
            <p className="mb-3 text-xs text-muted-foreground">
              {t('topup.intro')}{' '}
              <Trans i18nKey="topup.rate" ns="shop" components={{ strong: <span className="font-semibold" /> }} />
            </p>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-5" data-testid="topup-amount-grid">
              {packages.map((pkg) => (
                <button
                  key={pkg.eur}
                  type="button"
                  onClick={() => setSelected(pkg.eur)}
                  className={`flex flex-col items-start rounded-xl border px-3 py-3 text-left transition ${
                    selected === pkg.eur
                      ? 'border-amber-500 bg-amber-500/15 gold-text shadow-md'
                      : 'border-border bg-background text-foreground hover:bg-secondary'
                  }`}
                  data-testid={`topup-amount-${pkg.eur}`}
                >
                  <span className="text-lg font-bold tabular-nums">{t('topup.package_credits', { credits: pkg.credits })}</span>
                  <span className="text-[11px] text-muted-foreground">€{Number(pkg.eur).toFixed(2)}</span>
                </button>
              ))}
            </div>
            <Button
              onClick={startTopup}
              disabled={busy === 'topup' || !writable}
              className="mt-4 w-full gold-gradient text-black sm:w-auto"
              data-testid="topup-start-btn"
            >
              {busy === 'topup'
                ? <Loader2 className="h-4 w-4 animate-spin" />
                : (<><Sparkles className="mr-2 h-4 w-4" /> {t('topup.pay_button', { eur: selected })} <ArrowRight className="ml-2 h-4 w-4" /></>)
              }
            </Button>
          </>
        )}
      </section>

      {/* Phase 44 · Feb 2026 — The refund request area has moved to the
          WalletPage (below "Ordini"). Keeping this page focused on
          top-up discovery / package selection. */}
    </div>
  );
};

export default ShopPage;
